import { LLMProvider, ReportContextProvider } from '@ltvco/refresh-lib/ctx';
import { useReport } from '@ltvco/refresh-lib/hooks';
import { Grid } from '@ltvco/refresh-lib/theme';
import {
  isZeroed,
  useScrollToSectionOnNavigate,
} from '@ltvco/refresh-lib/utils';
import {
  buildPreForeclosureTab,
  buildRecentlySoldHomeTab,
  DebugMenu,
  MarketInsights,
  MonitoringBanner,
  NotesSection,
  peopleTypes,
  PerksBannerSection,
  PropertyReport,
  PropertyResults,
  PropertySmartInsightsContainer,
  ReportChangesOverview,
  ReportLoading,
  SerializedListingsData,
  SponsoredAgentForm,
  useReportMonitors,
  useSearchListings,
  useSession,
  useTuxedo,
} from '@ltvco/refresh-lib/v1';
import {
  PdfSection,
  ReportNullState,
  PropertyReportOverview,
  PropertyOwnersSection,
  ResidentsSection,
  BuildingPermits as BuildingPermitsV2,
  HomeOwnerAssociationSection,
  PropertyLiensSection,
  PropertySchools,
  NaturalHazards,
  DeedsSection,
  AssessedValue,
  PropertyValuationSection,
  PropertyDemographicsSection,
  PropertyLotBuildingSection,
  EditPropertyDetailsModal,
  ReportRating,
} from '@ltvco/refresh-lib/v2';
import { useFeatureIsOn } from '@ltvco/refresh-lib/vendors';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { ReportProps } from 'components/ChatBV/types';
import { getEmulatedUseQueryResult } from 'components/ChatBV/utils';
import { getPropertyNavLinkData } from 'navLinkData/propertyNavLinkData';
import { OwnerSearchGenericCTA } from 'routes/Dashboard/OwnerSearch/CTA/OwnerSearchGenericCTA';
import {
  groupAvmsBySource,
  getPropertyDetailsCount,
  getPropertyValuesCount,
} from 'utils/propertyReportUtils';
import { useQueryParams } from 'utils/useQueryParams';

export function PropertyChatBVReport({ permalink, reportData }: ReportProps) {
  // TODO: all this monitored report logic needs a refactor.
  // There is no point in refetching a report when a user toggles monitoring, so we use the initial value.
  const { reportIsMonitored } = useReportMonitors();
  const emulatedQueryResult = reportData
    ? getEmulatedUseQueryResult(reportData, true, false, false)
    : null;

  const queryReportResult =
    emulatedQueryResult || (useReport(permalink) as any);

  const navigate = useNavigate();
  const isInTaxHistoryVariation = useFeatureIsOn('prop-1877');
  const isInPropertySmartInsightsVariation = useFeatureIsOn('prop-2296');
  const isInOwnerSearchVariation = useFeatureIsOn('prop-2561');

  const zeroedReport = isZeroed(queryReportResult as any);

  const [city, setCity] = useState('');
  const [state, setState] = useState('');

  const queryParams = useQueryParams();

  const { hash } = useLocation();
  useScrollToSectionOnNavigate(hash, queryReportResult as any);

  const fullAddress = generateFullAddressFromParams(queryParams);
  const firstProperty = queryReportResult?.data?.entities?.properties?.[0];

  const {
    userProfile,
    homeDataSet,
    homeAdditions,
    setupUserProfile,
    createUserProfile,
  } = useTuxedo();
  const { session } = useSession();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const postHomeData = homeDataSet;
  const [temporaryPermalink, setTemporaryPermalink] = useState<string | null>(
    null
  );
  const [hasTriggeredMutation, setHasTriggeredMutation] = useState(false);
  const [userProfileTuxedo, setUserProfileTuxedo] = useState(null);
  const tuxedoBuilding = homeAdditions(temporaryPermalink) || { data: null };
  const [readyToFetch, setReadyToFetch] = useState(false);

  const searchData = {
    // 'byram' is the name of the DD Plan which includes Listhub data
    // to the listings endpoints response (WC - 2023/10/05).
    dd_plan: 'byram',
    rentals_limit: '10',
    pfc_limit: '10',
    listings_limit: '10',
    city,
    state,
    brand: 'nw',
  };

  const { data: searchListingsResult = [] } = useSearchListings(
    searchData,
    Boolean(city && state)
  );

  useEffect(() => {
    setCity(firstProperty?.parcel_address?.parsed?.city || '');
    setState(firstProperty?.state || '');
  }, [firstProperty?.parcel_address?.parsed?.city, firstProperty?.state]);

  useEffect(() => {
    if (!firstProperty || hasTriggeredMutation) {
      return;
    }

    if (!readyToFetch) {
      setReadyToFetch(true);
    }
  }, [firstProperty, hasTriggeredMutation]);

  // Handling the user profile creation and query success
  useEffect(() => {
    if (readyToFetch) {
      if (userProfile.isError && !hasTriggeredMutation) {
        if (
          !userProfile?.data?.user_profile &&
          session?.account?.account?.user_info &&
          firstProperty
        ) {
          const userProfileData = setupUserProfile(
            session.account.account.user_info,
            firstProperty.mailing_address
          );

          userProfileData.record_search = {
            address: firstProperty?.parcel_address?.full || '',
            city: firstProperty?.parcel_address?.parsed?.city || '',
            state: firstProperty?.parcel_address?.parsed?.state || '',
            zip_code: firstProperty?.parcel_address?.parsed?.zip5 || '',
          };

          createUserProfile.mutate({
            userProfile: {
              user_profile: userProfileData,
              record_search: userProfileData.record_search,
            },
          });

          setHasTriggeredMutation(true);
        }
      }

      if (userProfile.isSuccess && userProfile.data?.user_profile) {
        setTemporaryPermalink(permalink);
        setUserProfileTuxedo(userProfile.data.user_profile);
      }
    }
  }, [readyToFetch, userProfile, session, firstProperty, createUserProfile]);

  // Update state when userProfile fetch succeeds
  useEffect(() => {
    if (userProfile.isSuccess && userProfile.data?.user_profile) {
      setTemporaryPermalink(permalink);
      setUserProfileTuxedo(userProfile.data.user_profile);
    }
  }, [userProfile, permalink]);

  // Handling main report fetched
  if (queryReportResult.isLoading || queryReportResult.isError) {
    return <ReportLoading menuItems={10} />;
  }

  if (zeroedReport) {
    return <ReportNullState />;
  }

  const handleOpenEditModal = () => {
    setIsEditModalOpen(!isEditModalOpen);
  };

  const report = new PropertyReport(queryReportResult?.data);

  if (!report) {
    return <ReportLoading menuItems={10} />;
  }

  const property = report.data.properties[0];
  const responseHasPropertyDataAndNoEntitiesFlagIsNull =
    (report.data?.meta?.counts?.properties ?? 0) > 0 &&
    queryReportResult?.data?.meta?.no_entities === null;
  const noEntitiesFlagIsEitherTrueOrNull =
    queryReportResult?.data?.meta?.no_entities ||
    queryReportResult?.data?.meta?.no_entities === null;

  if (
    !responseHasPropertyDataAndNoEntitiesFlagIsNull &&
    noEntitiesFlagIsEitherTrueOrNull
  ) {
    const options = queryReportResult?.data?.entities?.options;
    const fromParam = queryParams.get('from') || '';
    return (
      <ReportContextProvider permalink={permalink} reportType="property">
        <Grid
          sx={{
            bgcolor: 'background.default',
            flexDirection: 'column',
            paddingBottom: '20px',
            px: 2,
          }}
          container
        >
          <PropertyResults
            searchAddress={report?.data?.address || fullAddress}
            options={options || { businesses: [], properties: [], people: [] }}
            fromDashboard={fromParam}
            pageOptions={{
              disableSubheadline: Boolean(
                options?.properties?.length && options.properties.length < 1
              ),
            }}
            isChatPage
          />
        </Grid>
      </ReportContextProvider>
    );
  }

  const userProfileForEditModal = userProfileTuxedo
    ? userProfileTuxedo
    : setupUserProfile(
        session.account?.account?.user_info,
        property?.mailing_address
      );
  let updatedBuilding = property?.buildings?.[0];

  if (
    userProfileTuxedo &&
    tuxedoBuilding?.data?.user_data_sets?.user_property_detail_data_set
  ) {
    const totalRoomsValue =
      tuxedoBuilding?.data?.user_data_sets?.user_property_detail_data_set
        ?.custom_attributes?.beds +
      tuxedoBuilding?.data?.user_data_sets?.user_property_detail_data_set
        ?.custom_attributes?.baths;

    const updatedBuildingTuxedo = {
      ...updatedBuilding,
      rooms: {
        ...updatedBuilding?.rooms,
        bed: tuxedoBuilding?.data?.user_data_sets?.user_property_detail_data_set
          ?.custom_attributes?.beds,
        baths: {
          ...updatedBuilding?.rooms?.baths,
          total:
            tuxedoBuilding?.data?.user_data_sets?.user_property_detail_data_set
              ?.custom_attributes?.baths,
        },
        total: totalRoomsValue,
      },
      area: tuxedoBuilding?.data?.user_data_sets?.user_property_detail_data_set
        ?.custom_attributes?.sqft,
    };
    updatedBuilding = updatedBuildingTuxedo;
  }

  const displayAddress = report.data.address || fullAddress;
  const parsedPropertyAddress = {
    street: property?.parcel_address?.parts?.street || '',
    city: property?.parcel_address?.parts?.city || '',
    state: property?.parcel_address?.parts?.state || '',
    zip5: property?.parcel_address?.parts?.zip || '',
  };
  const marketValues = property.market;
  const marketSortedBySource = groupAvmsBySource(marketValues);
  const propertyDetailsCount = getPropertyDetailsCount(property);
  const PropertyValuesCount = getPropertyValuesCount(property);
  const propertyNavLinkData = getPropertyNavLinkData(
    property?.owners.length,
    property?.residents.length,
    property?.building_permits ? property?.building_permits.length : 0,
    property?.deeds.length,
    property?.courts.civils.length,
    propertyDetailsCount || 0,
    PropertyValuesCount || 0,
    report?.data?.properties?.[0]?.equities?.length || 0,
    report?.data?.schools?.length || 0
  );

  // MarketInsights data
  const listingsFetchResults = (searchListingsResult as SerializedListingsData)
    ?.listing_teaser;
  const preforeclosureData = listingsFetchResults?.preforeclosures || [];
  const totalPreforeclosures =
    listingsFetchResults?.meta?.available?.market?.preforeclosures || 0;

  const listingsNavigateAction = (property: any, type?: string) => {
    const { city, state, zip5 } = property?.parcel_address?.parsed || {
      city: '',
      state: '',
      zip5: '',
    };
    const url = `/search/listing?city=${city}&state=${state}&zip5=${zip5}&search_type=${
      type || 'pre_foreclosures'
    }`;
    navigate(url);
  };

  const marketInsightsTabs = [
    buildRecentlySoldHomeTab({
      property,
      data: property.comparables,
    }),
    buildPreForeclosureTab({
      property,
      data: preforeclosureData,
      navigateAction: listingsNavigateAction,
      totalDataValues: totalPreforeclosures,
    }),
  ];

  document.title = `${displayAddress} - BeenVerified`;

  return (
    <LLMProvider llmData={property} llmType="property">
      <Grid
        size={{ sm: 12, md: 8, lg: 8 }}
        marginTop={0.5}
        sx={{
          '&.MuiGrid-item': {
            paddingTop: { xs: 3.75, md: 7 },
          },
        }}
      >
        {reportIsMonitored && (
          <ReportChangesOverview
            permalink={permalink}
            navLinkData={propertyNavLinkData}
          />
        )}
        <PropertyReportOverview
          propertyReportData={property}
          serializedPropertyReportData={report.data}
          permalink={permalink}
          updatedBuilding={updatedBuilding}
          marketValues={marketValues}
          allowClaiming={false}
        />
        <SponsoredAgentForm
          parsedPropertyAddress={parsedPropertyAddress}
          propertyFullAddress={displayAddress}
        />
        {/* disabling this for now as tophap widgets are not working */}
        {/* <PropertyNeighborhoodData propertyReportData={property} /> */}
        <MonitoringBanner
          propertyReportData={property}
          permalink={permalink}
          reportType="property"
        />
        {isInPropertySmartInsightsVariation && (
          <PropertySmartInsightsContainer reportData={report.data} />
        )}
        {isInOwnerSearchVariation && <OwnerSearchGenericCTA />}
        <PropertyOwnersSection
          owners={property.owners as peopleTypes.Identities}
          residents={property.residents}
          deeds={property.deeds}
          propertyAddress={displayAddress}
          permalink={permalink}
        />
        <ResidentsSection
          residents={property.residents}
          propertyAddress={displayAddress}
          permalink={permalink}
        />
        <PropertyLotBuildingSection
          propertyAddress={displayAddress}
          property={property}
          updatedBuilding={updatedBuilding}
          canEditProperty={true}
          setIsEditModalOpen={handleOpenEditModal}
        />
        <BuildingPermitsV2
          propertyAddress={displayAddress}
          property={property}
          permalink={permalink}
        />
        <HomeOwnerAssociationSection hoas={property.hoas} />
        <AssessedValue
          property={property}
          useTaxHistory={isInTaxHistoryVariation}
        />
        <PropertyValuationSection
          property={property}
          propertyAddress={displayAddress}
          marketValues={marketValues}
          rentalValues={property.values?.rental || []}
          avmsPartners={marketSortedBySource}
          permalink={permalink}
        />
        <DeedsSection deeds={property.deeds} />
        <PropertyLiensSection
          fullAddress={displayAddress}
          liens={property.courts}
          permalink={permalink}
        />
        <MarketInsights
          propertyAddress={displayAddress}
          tabs={marketInsightsTabs.filter((item) => !!item)}
        />
        <PropertyDemographicsSection
          property={property}
          propertyAddress={displayAddress}
        />
        <PropertySchools
          propertyAddress={displayAddress}
          property={report.data}
        />
        {/* <HomesNearby />
        <SurroundingArea /> */}
        <NaturalHazards property={property} />
        <PerksBannerSection permalink={permalink} />
        <PdfSection permalink={permalink} />
        <NotesSection permalink={permalink} />
        <ReportRating
          rating={report.data.meta?.rating || null}
          report_type="property"
        />
        {/* <Comments /> */}
        <EditPropertyDetailsModal
          open={isEditModalOpen}
          setIsOpen={handleOpenEditModal}
          building={updatedBuilding}
          permalink={permalink}
          userProfile={userProfileForEditModal}
          postHomeData={postHomeData}
          propertyAddress={displayAddress}
        />
      </Grid>
      <DebugMenu menuItems={report.data.rawData.debug_menu} />
    </LLMProvider>
  );
}

function generateFullAddressFromParams(queryParams: URLSearchParams): string {
  const address = queryParams.get('address') || '';
  const city = queryParams.get('city') || '';
  const state = queryParams.get('state') || '';
  const zipcode = queryParams.get('zipcode') || '';

  return `${address} ${city}, ${state} ${zipcode}`;
}
